<template>
  <div class="table-formatter flex-grow-1">
    <b-table
      :key="allowCustomSort"
      :items="items"
      :fields="fields"
      small
      hover
      :thead-class="theadClass"
      :tbody-tr-class="tbodyTrClass"
      :sort-by="sortBy"
      :sort-desc="isChild ? sortDesc : null"
      :foot-clone="showFooter"
      no-footer-sorting
      @row-clicked="updatePath"
    >
      <template #foot(name)>
        {{ childTotal.name }}
      </template>
      <template #foot(value)>
        {{ formatValueByColumn(childTotal.column, childTotal.value) }}
      </template>
    </b-table>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

// Note: :key="allowCustomSort" is a workaround for this issue https://github.com/bootstrap-vue/bootstrap-vue/issues/5520

import { SummaryTableOptions } from '@/types'
import { SUMMARY_TABLE_CONFIG_KEY } from '@/constants'

type ChildTotal = {
  isTotal: boolean
  name: string
  value: number
  column: string
  _rowVariant: string
}

const props = {
  items: {
    type: Array,
    default: () => [],
  },
  labelName: {
    type: String,
    default: undefined,
  },
  labelValue: {
    type: String,
    default: undefined,
  },
  display: {
    type: String,
    default: SummaryTableOptions.CHILD,
  },
  isRoot: Boolean,
  showFooter: Boolean,
  childTotal: {
    type: Object as PropType<ChildTotal>,
    required: true,
  },
}

@Component
export default class TableFormatter extends Vue.extend({ props }) {
  get displayHeaders() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[
      SUMMARY_TABLE_CONFIG_KEY
    ].displayHeaders
  }

  get allowCustomSort() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[
      SUMMARY_TABLE_CONFIG_KEY
    ].allowCustomSort
  }

  get sortBySetting() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[
      SUMMARY_TABLE_CONFIG_KEY
    ].sortBy
  }

  get sortDesc() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[
      SUMMARY_TABLE_CONFIG_KEY
    ].sortDesc
  }

  get sortTabOrder() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[
      SUMMARY_TABLE_CONFIG_KEY
    ].sortTabOrder
  }

  get formatValueByColumn() {
    return this.$typedStore.visualisationData.formatValueByColumn
  }

  get fields() {
    return [
      {
        key: 'name',
        label: this.labelName,
        sortable: this.allowCustomSort,
      },
      {
        key: 'value',
        label: this.labelValue,
        sortable: this.allowCustomSort,
        formatter: this.formatter,
        class: 'summary-table-value',
      },
    ]
  }

  get isChild() {
    return this.display === SummaryTableOptions.CHILD
  }

  get theadClass() {
    return this.displayHeaders && this.isChild ? '' : 'hide-table-header'
  }

  get cursorClass() {
    return this.display === SummaryTableOptions.CHILD ? 'cursor-pointer' : ''
  }

  get sortBy() {
    if (!this.isChild) {
      return null
    }
    if (this.isRoot && this.sortTabOrder) {
      return null
    }
    return this.sortBySetting
  }

  // @ts-ignore TODO bootstrap vue b-table component investigation
  async updatePath(item) {
    if (item.path) await this.$typedStore.activeVisualisation.updateStoryPath(item.path)
  }

  // @ts-ignore TODO bootstrap vue b-table component investigation
  formatter(value, key, item) {
    return this.formatValueByColumn(item.column, +value)
  }

  // @ts-ignore TODO bootstrap vue b-table component investigation
  tbodyTrClass(item) {
    return item.isTotal ? '' : this.cursorClass
  }
}
</script>

<style lang="scss">
.table-formatter {
  thead {
    tr {
      th {
        border-bottom: 1px solid #dee2e6;
        border-top: none;
        font-weight: 600;
        color: #455;
      }
    }
  }
}

.hide-table-header {
  display: none;
}

.summary-table-value {
  text-align: right;
}

table.table-hover tbody tr:hover {
  color: white;
}

.hide-table-header + tbody tr:first-child td {
  border-top-color: rgba(0, 0, 0, 0);
}

.cursor-pointer {
  cursor: pointer;
}
</style>
